import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import './embed.css';

/**
 * EmbedContent - Fullscreen content pages for each party phase
 *
 * Routes:
 *   /embed/content/:contentId
 *
 * Content IDs:
 *   - registration
 *   - orientation
 *   - trading-1, trading-2, trading-3, trading-4
 *   - portfolio-warfare
 *   - dead-cat-bounce
 *   - a-brief-case
 *   - liquidity-crisis
 *   - closing
 *   - winners
 *
 * URL Parameters:
 *   - muted: 'true' or 'false' (default: true) - for videos
 *   - autoplay: 'true' or 'false' (default: true) - for videos
 *   - loop: 'true' or 'false' (default: true) - for videos/carousels
 *   - fit: 'cover', 'contain', or 'fill' (default: cover)
 *   - bg: background color (default: #000000)
 *   - interval: carousel auto-advance interval in ms (default: 5000, 0 to disable)
 */

// Content configuration for each phase
// Types: 'video', 'slides', 'carousel', 'placeholder'
const CONTENT_CONFIG = {
  'registration': {
    type: 'video',
    title: 'Registration',
    url: '/videos/registration.mp4',
  },
  'orientation': {
    type: 'placeholder',
    title: 'Orientation',
    // Change to 'slides' and add url when ready:
    // type: 'slides',
    // url: 'https://docs.google.com/presentation/d/e/YOUR_ID/embed?start=false&loop=false&delayms=3000',
  },
  'trading-1': {
    type: 'placeholder',
    title: 'Trading Block 1 - Asian Session',
  },
  'trading-2': {
    type: 'placeholder',
    title: 'Trading Block 2 - European Session',
  },
  'trading-3': {
    type: 'placeholder',
    title: 'Trading Block 3 - Canadian Session',
  },
  'trading-4': {
    type: 'placeholder',
    title: 'Trading Block 4 - Power Hour',
  },
  'portfolio-warfare': {
    type: 'placeholder',
    title: 'Portfolio Warfare',
    // Change to 'carousel' and add images when ready:
    // type: 'carousel',
    // images: ['/images/games/pw-1.png', '/images/games/pw-2.png'],
  },
  'dead-cat-bounce': {
    type: 'placeholder',
    title: 'Dead Cat Bounce',
  },
  'a-brief-case': {
    type: 'placeholder',
    title: 'A Brief Case',
  },
  'liquidity-crisis': {
    type: 'placeholder',
    title: 'Liquidity Crisis',
  },
  'closing': {
    type: 'placeholder',
    title: 'Closing Bell',
  },
  'winners': {
    type: 'placeholder',
    title: 'Winners',
  },
};

// Video Player Component
const VideoContent = ({ config, muted, autoplay, loop, fit }) => {
  const handleEnded = (e) => {
    // Fallback: restart video if loop didn't work
    if (loop) {
      e.target.currentTime = 0;
      e.target.play();
    }
  };

  return (
    <video
      src={config.url}
      muted={muted}
      autoPlay={autoplay}
      loop={loop}
      playsInline
      controls
      onEnded={handleEnded}
      onDoubleClick={(e) => {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          e.target.requestFullscreen?.();
        }
      }}
      style={{
        width: '100%',
        height: '100%',
        objectFit: fit,
        position: 'absolute',
        top: 0,
        left: 0,
      }}
    />
  );
};

// Google Slides Embed Component
const SlidesContent = ({ config }) => {
  return (
    <iframe
      src={config.url}
      title={config.title}
      frameBorder="0"
      allowFullScreen
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        position: 'absolute',
        top: 0,
        left: 0,
      }}
    />
  );
};

// Image Carousel Component
const CarouselContent = ({ config, loop, interval, fit }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = config.images || [];

  const goToNext = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex(prev => {
      if (prev >= images.length - 1) {
        return loop ? 0 : prev;
      }
      return prev + 1;
    });
  }, [images.length, loop]);

  const goToPrev = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex(prev => {
      if (prev <= 0) {
        return loop ? images.length - 1 : prev;
      }
      return prev - 1;
    });
  }, [images.length, loop]);

  // Auto-advance
  useEffect(() => {
    if (interval > 0 && images.length > 1) {
      const timer = setInterval(goToNext, interval);
      return () => clearInterval(timer);
    }
  }, [interval, images.length, goToNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrev]);

  if (images.length === 0) {
    return (
      <div className="embed-placeholder">
        <div className="placeholder-text">No images configured</div>
      </div>
    );
  }

  return (
    <div
      className="embed-carousel"
      onClick={goToNext}
      style={{
        cursor: 'pointer',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }}
    >
      <img
        src={images[currentIndex]}
        alt={`${config.title} - ${currentIndex + 1}`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: fit,
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      />

      {/* Navigation dots */}
      {images.length > 1 && (
        <div className="carousel-dots">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
            />
          ))}
        </div>
      )}

      {/* Progress indicator */}
      <div className="carousel-progress">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};

// Placeholder Component
const PlaceholderContent = ({ config }) => {
  return (
    <div
      className="embed-placeholder"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }}
    >
      <div className="placeholder-icon">📺</div>
      <div className="placeholder-title">{config.title}</div>
      <div className="placeholder-text">Content coming soon</div>
      <div className="placeholder-hint">
        Configure in EmbedContent.js
      </div>
    </div>
  );
};

const EmbedContent = () => {
  const { contentId } = useParams();
  const [searchParams] = useSearchParams();

  // URL params
  const muted = searchParams.get('muted') !== 'false';
  const autoplay = searchParams.get('autoplay') !== 'false';
  const loop = searchParams.get('loop') !== 'false';
  const fit = searchParams.get('fit') || 'cover';
  const bg = searchParams.get('bg') || '#000000';
  const interval = parseInt(searchParams.get('interval') || '5000', 10);

  const config = CONTENT_CONFIG[contentId];

  if (!config) {
    return (
      <div
        className="embed-content"
        style={{
          background: bg,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <div style={{ fontSize: '48px' }}>404</div>
        <div>Unknown content: {contentId}</div>
        <div style={{ fontSize: '14px', opacity: 0.7 }}>
          Available: {Object.keys(CONTENT_CONFIG).join(', ')}
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (config.type) {
      case 'video':
        return (
          <VideoContent
            config={config}
            muted={muted}
            autoplay={autoplay}
            loop={loop}
            fit={fit}
          />
        );
      case 'slides':
        return <SlidesContent config={config} />;
      case 'carousel':
        return (
          <CarouselContent
            config={config}
            loop={loop}
            interval={interval}
            fit={fit}
          />
        );
      case 'placeholder':
      default:
        return <PlaceholderContent config={config} />;
    }
  };

  return (
    <div
      className="embed-content"
      style={{
        background: bg,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {renderContent()}
    </div>
  );
};

export default EmbedContent;
